import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { EmptyState, FilterSelect, HpCardHeader, TableScroll } from '@hp/ui';
import { useCan } from '../../api/me';
import { trpc } from '../../api/trpc';
import { ErrorAlert } from '../../components/error-alert';
import { Spinner } from '../../components/ui/icons';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';
import { fmtInt } from '../../lib/format';
import { useLeadErrorText } from './error-text';
import { marketFrom } from './labels';
import { invalidateLeads, useAdAccounts, useMarketDefaults, type AdAccount } from './queries';

/**
 * Card "Ad accounts (Meta)": which market each Meta ad account serves, i.e. which channel and market
 * default apply to its campaigns (the rules tab's market filter reads it). Managers change it with a
 * select that saves on change; everybody else sees the market's name.
 */
export function AccountsCard() {
  const { t } = useTranslation(['leads', 'common']);
  const describe = useLeadErrorText();
  const canManage = useCan()('leads.manage');
  const accounts = useAdAccounts();
  // Market names come from the API: one market default per market.
  const markets = (useMarketDefaults().data ?? []).flatMap((d) =>
    d.marketCode ? [{ code: d.marketCode, label: d.marketLabel ?? d.marketCode }] : [],
  );
  const [saved, setSaved] = useState<AdAccount | null>(null);
  const setMarket = useMutation(
    trpc.leads.accounts.setMarket.mutationOptions({
      onSuccess: (account) => setSaved(account),
      onSettled: invalidateLeads,
    }),
  );
  const rows = accounts.data ?? [];

  return (
    <section className="hp-card">
      <HpCardHeader title={t('general.accounts.title')} subtitle={t('general.accounts.subtitle')} icon="megaphone" />
      <ErrorAlert error={setMarket.error ?? accounts.error} describe={describe} className="bo-alert--inset" />
      {accounts.isLoading ? (
        <p className="hp-card__body">
          <Spinner>{t('common:actions.loading')}</Spinner>
        </p>
      ) : rows.length === 0 ? (
        <EmptyState icon="megaphone" title={t('general.accounts.emptyTitle')}>
          {t('general.accounts.emptyBody')}
        </EmptyState>
      ) : (
        <TableScroll label={t('general.accounts.title')}>
          <BrutTable>
            <BrutTheadRow>
              <th>{t('general.accounts.columns.account')}</th>
              <th className="bo-num">{t('general.accounts.columns.campaigns')}</th>
              <th>{t('general.accounts.columns.market')}</th>
            </BrutTheadRow>
            <tbody>
              {rows.map((account) => {
                const busy = setMarket.isPending && setMarket.variables.accountId === account.id;
                return (
                  <tr key={account.id}>
                    <td>
                      <span className="bo-strong">{account.name}</span>
                      <br />
                      <small className="bo-muted bo-mono">{account.externalId}</small>
                    </td>
                    <td className="bo-num">
                      {t('general.accounts.campaignCounts', {
                        running: fmtInt(account.runningCampaigns),
                        total: fmtInt(account.campaigns),
                      })}
                    </td>
                    <td>
                      {canManage ? (
                        <span className="bo-row">
                          <FilterSelect
                            value={account.marketCode ?? ''}
                            disabled={busy}
                            aria-label={t('general.accounts.marketAria', { name: account.name })}
                            onChange={(e) => {
                              setSaved(null);
                              setMarket.mutate({
                                accountId: account.id,
                                marketCode: marketFrom(e.target.value, markets),
                              });
                            }}
                          >
                            <option value="">{t('rules.noMarket')}</option>
                            {markets.map((m) => (
                              <option key={m.code} value={m.code}>
                                {m.label}
                              </option>
                            ))}
                          </FilterSelect>
                          {busy && <Spinner>{t('general.accounts.saving')}</Spinner>}
                        </span>
                      ) : (
                        (account.marketLabel ?? <span className="bo-muted">{t('rules.noMarket')}</span>)
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </BrutTable>
        </TableScroll>
      )}
      <div className="hp-card__body bo-sections">
        {saved && (
          <p className="bo-notice" role="status">
            {t('general.accounts.saved', { name: saved.name, market: saved.marketLabel ?? t('rules.noMarket') })}
          </p>
        )}
        <p className="bo-hint">{t('general.accounts.hint')}</p>
      </div>
    </section>
  );
}
